import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  InputAdornment,
  TextField,
  Typography,
} from '../../primitives';
import { palette } from '../../theme/palette';
import { usePortalPalette } from '../../theme/usePortalPalette';
import { visuallyHidden } from '../../theme/focus';
import * as Icons from '../../icons';
import { fuzzyFilter } from './fuzzy';
import type { CommandPaletteItem, CommandPaletteLabels, CommandPaletteSearch } from './types';
import { iconSize } from '@munifocus/tokens';

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  /**
   * Static navigation/actions for this portal. Filtered and ranked locally on
   * every keystroke, and shown unfiltered while the query is empty so the
   * palette is useful before the reader types.
   */
  commands?: readonly CommandPaletteItem[];
  /**
   * Remote lookup for entity hits. Debounced; the previous request is aborted
   * when a newer keystroke supersedes it. Omit for a commands-only palette.
   */
  search?: CommandPaletteSearch;
  /** Called with the chosen row. The palette closes itself first. */
  onSelect: (item: CommandPaletteItem) => void;
  /** Localized heading per group key. A group with no entry renders unlabelled. */
  groupLabels: Readonly<Record<string, string>>;
  /** Group render order. Groups not listed here fall to the end, in first-seen order. */
  groupOrder?: readonly string[];
  labels: CommandPaletteLabels;
  /** Below this many characters the remote search is not called. */
  minQueryLength?: number;
  debounceMs?: number;
}

const DEFAULT_DEBOUNCE_MS = 250;
const DEFAULT_MIN_QUERY_LENGTH = 2;

interface RenderGroup {
  key: string;
  label: string | undefined;
  items: CommandPaletteItem[];
}

/**
 * Bucket a flat, already-ordered list into groups without disturbing the order
 * inside each one. Ranking happens before this, so the flat list is the source
 * of truth for "what is item N" — grouping is purely presentational.
 */
function toGroups(
  items: readonly CommandPaletteItem[],
  groupLabels: Readonly<Record<string, string>>,
  groupOrder: readonly string[] | undefined,
): RenderGroup[] {
  const buckets = new Map<string, CommandPaletteItem[]>();
  for (const item of items) {
    const bucket = buckets.get(item.group);
    if (bucket) bucket.push(item);
    else buckets.set(item.group, [item]);
  }
  const seen = [...buckets.keys()];
  const ordered = groupOrder
    ? [...seen].sort((a, b) => {
        const ai = groupOrder.indexOf(a);
        const bi = groupOrder.indexOf(b);
        // Unlisted groups sort after listed ones, keeping their first-seen order.
        return (
          (ai === -1 ? groupOrder.length + seen.indexOf(a) : ai) -
          (bi === -1 ? groupOrder.length + seen.indexOf(b) : bi)
        );
      })
    : seen;
  return ordered.map((key) => ({
    key,
    label: groupLabels[key],
    items: buckets.get(key) ?? [],
  }));
}

/**
 * The Cmd-K / Ctrl-K global palette: one input over a grouped, keyboard-driven
 * result list that mixes locally-filtered commands with debounced remote hits.
 *
 * App-agnostic by construction — it takes its commands, its search function,
 * and every visible string as props, so gov, admin, and web share one widget
 * without `@munifocus/ui` learning anything about routes or endpoints.
 *
 * Accessibility follows the ARIA combobox-with-listbox pattern: focus stays in
 * the text input the whole time and the "cursor" is virtual, published via
 * `aria-activedescendant`. That is what lets a screen-reader user type and
 * arrow through results in one motion. `Dialog` supplies the modal focus trap
 * and restores focus to the trigger on close.
 */
export function CommandPalette({
  open,
  onClose,
  commands = [],
  search,
  onSelect,
  groupLabels,
  groupOrder,
  labels,
  minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: CommandPaletteProps) {
  const portal = usePortalPalette();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [remote, setRemote] = useState<CommandPaletteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const listRef = useRef<HTMLDivElement | null>(null);

  const trimmed = query.trim();
  const shouldSearch = Boolean(search) && trimmed.length >= minQueryLength;

  // Each open starts clean: a palette that remembered last week's query would
  // hide the default command list behind stale results.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setRemote([]);
    setLoading(false);
    setActiveIndex(0);
  }, [open]);

  // Debounced remote lookup. The abort controller matters as much as the timer:
  // without it a slow early request can land after a fast later one and
  // overwrite the results for a query the reader has already moved past.
  useEffect(() => {
    if (!open || !search) return;
    if (!shouldSearch) {
      setRemote([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      search(trimmed, controller.signal)
        .then((items) => {
          if (controller.signal.aborted) return;
          setRemote(items);
          setActiveIndex(0);
        })
        .catch(() => {
          // A failed lookup degrades to "no remote hits" rather than breaking
          // the palette — the local commands must keep working offline.
          if (!controller.signal.aborted) {
            setRemote([]);
            setActiveIndex(0);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, debounceMs);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [open, search, shouldSearch, trimmed, debounceMs]);

  // Commands are ranked locally; remote hits keep the server's ordering (it
  // matched on fields we can't see, like a bulletin body).
  const items = useMemo(
    () => [...fuzzyFilter(trimmed, commands), ...remote],
    [trimmed, commands, remote],
  );

  const groups = useMemo(
    () => toGroups(items, groupLabels, groupOrder),
    [items, groupLabels, groupOrder],
  );
  // Re-flattened from the groups so the keyboard index matches visual order
  // exactly, however the groups were reordered.
  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  const clampedIndex = flat.length === 0 ? -1 : Math.min(activeIndex, flat.length - 1);
  const activeItem = clampedIndex >= 0 ? flat[clampedIndex] : undefined;
  const activeDescendant = activeItem ? `${baseId}-option-${activeItem.id}` : undefined;

  // Keep the virtual cursor inside the scroll viewport.
  useEffect(() => {
    if (!activeDescendant || !listRef.current) return;
    const row = listRef.current.querySelector(`#${CSS.escape(activeDescendant)}`);
    // Guarded rather than called blind: `scrollIntoView` is absent in jsdom, and
    // a throw here would abort the commit and silently drop the cursor move that
    // triggered it — the effect must never be able to break navigation.
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [activeDescendant]);

  const choose = (item: CommandPaletteItem) => {
    onClose();
    onSelect(item);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (flat.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        // Wrap: from the last row, down returns to the first. A long result
        // list is faster to reach from the bottom than by arrowing back up.
        setActiveIndex((i) => (i + 1) % flat.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(flat.length - 1);
        break;
      case 'Enter':
        if (activeItem) {
          event.preventDefault();
          choose(activeItem);
        }
        break;
      default:
        break;
    }
  };

  /**
   * The single line shown in place of the list, or `null` when there are rows
   * to render. Resolved once, in priority order, rather than as nested ternaries
   * at the call site: "searching" outranks "nothing found", which outranks the
   * pre-typing prompt.
   */
  const placeholderMessage: string | null = (() => {
    if (flat.length > 0) return null;
    if (loading) return labels.loading;
    return trimmed === '' ? labels.empty : labels.noResults;
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-label={labels.title}
      slotProps={{
        paper: {
          sx: {
            // Pinned near the top rather than centred: the list grows downward,
            // so a centred dialog would visibly jump as results arrive.
            alignSelf: 'flex-start',
            mt: { xs: 4, sm: 10 },
            borderRadius: 2,
            border: `1px solid ${palette.border.line}`,
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          borderBottom: `1px solid ${palette.border.line}`,
        }}
      >
        <TextField
          autoFocus
          fullWidth
          variant="standard"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            // Reset the cursor here rather than in a post-render effect: any
            // change to the result set must move it back to the top *before*
            // the next keystroke can act on it, and an effect can land after.
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={labels.placeholder}
          aria-label={labels.title}
          slotProps={{
            // The ARIA combobox contract belongs on the native <input>, not on
            // MUI's wrapper div — `htmlInput` is the slot that reaches it.
            htmlInput: {
              role: 'combobox',
              // Tracks whether the popup is *displayed*, not whether it has
              // rows. The listbox is rendered for as long as the palette is
              // open, so reporting "collapsed" on an empty result set would
              // contradict `aria-controls`, which still points at a live
              // element — AT would be told to look at a list that isn't there.
              'aria-expanded': open,
              'aria-controls': listboxId,
              'aria-activedescendant': activeDescendant,
              'aria-autocomplete': 'list',
            },
            input: {
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <Icons.Search size={iconSize.lg} aria-hidden />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} aria-label={labels.loading} />
                </InputAdornment>
              ) : undefined,
              sx: { fontSize: 16, py: 1.5 },
            },
          }}
        />
      </Box>

      <Box ref={listRef} sx={{ maxHeight: 380, overflowY: 'auto', py: flat.length > 0 ? 1 : 0 }}>
        <Box id={listboxId} role="listbox" aria-label={labels.title}>
          {groups.map((group) => (
            <Box
              key={group.key}
              role="group"
              // Only when there is a heading to point at. A caller may leave a
              // group unlabelled (the prop contract allows it), and an
              // `aria-labelledby` referencing an element that was never
              // rendered is an invalid IDREF, not a harmless no-op.
              aria-labelledby={group.label ? `${baseId}-group-${group.key}` : undefined}
            >
              {group.label && (
                <Typography
                  id={`${baseId}-group-${group.key}`}
                  component="div"
                  sx={{
                    px: 2,
                    pt: 1.5,
                    pb: 0.75,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: palette.text.muted,
                  }}
                >
                  {group.label}
                </Typography>
              )}
              {group.items.map((item) => {
                const index = flat.indexOf(item);
                const isActive = index === clampedIndex;
                const Icon = item.icon;
                return (
                  // The keyboard path is the combobox input's arrow/Enter
                  // handling over aria-activedescendant, which the rule cannot see.
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <Box
                    key={item.id}
                    id={`${baseId}-option-${item.id}`}
                    role="option"
                    aria-selected={isActive}
                    // Pointer only — focus never leaves the input, so this row
                    // must not become a tab stop competing with it.
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => choose(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 2,
                      py: 1.25,
                      cursor: 'pointer',
                      transition: 'background-color 200ms',
                      backgroundColor: isActive ? palette.background.parch : 'transparent',
                      borderLeft: `2px solid ${isActive ? portal.brand.accent : 'transparent'}`,
                    }}
                  >
                    {Icon && (
                      <Icon
                        size={17}
                        strokeWidth={1.75}
                        aria-hidden
                        style={{ flexShrink: 0, color: palette.text.muted }}
                      />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: palette.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.title}
                      </Typography>
                      {item.subtitle && (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: 12.5,
                            color: palette.text.muted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {placeholderMessage !== null && (
          <Typography
            component="div"
            sx={{ px: 2, py: 4, textAlign: 'center', fontSize: 13.5, color: palette.text.muted }}
          >
            {placeholderMessage}
          </Typography>
        )}
      </Box>

      {/* Announces the result count without moving focus or stealing the cursor. */}
      <Box
        role="status"
        aria-live="polite"
        // The shared 1px style: `width: 1` in sx is 100%, not one pixel.
        sx={visuallyHidden}
      >
        {loading ? labels.loading : labels.resultCountLabel(flat.length)}
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderTop: `1px solid ${palette.border.line}`,
          backgroundColor: palette.background.default,
        }}
      >
        <Hint
          icon={<Icons.ArrowUp size={iconSize.xs} />}
          secondIcon={<Icons.ArrowDown size={iconSize.xs} />}
        >
          {labels.hintNavigate}
        </Hint>
        <Hint icon={<Icons.CornerDownLeft size={iconSize.xs} />}>{labels.hintSelect}</Hint>
        <Hint>{labels.hintClose}</Hint>
      </Box>
    </Dialog>
  );
}

function Hint({
  icon,
  secondIcon,
  children,
}: {
  icon?: React.ReactNode;
  secondIcon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      {secondIcon}
      <Typography
        component="span"
        sx={{ fontSize: 11.5, color: palette.text.muted, letterSpacing: '0.01em' }}
      >
        {children}
      </Typography>
    </Box>
  );
}
